(() => {
  'use strict';

  if (customElements.get('dieux-product-media-gallery')) return;

  /** Ceiling on holding a crossfade for image decode, so a stalled fetch degrades to today's pop-in. */
  const CROSSFADE_READY_TIMEOUT = 1000;

  const SLIDE_CONTROLS =
    'button, a, input, select, textarea, video, video-player-component, [contenteditable="true"]';

  let mediaSwapRequest = null;
  const loadMediaSwap = () => {
    mediaSwapRequest ??= (window.importShim ?? ((s) => import(s)))('@theme/media-swap');
    return mediaSwapRequest;
  };

  const rootMotionTokens = new Map();

  /**
   * Reads a root motion token once per reduced-motion state.
   *
   * @param {string} name - Custom property name, including the leading `--`.
   * @param {boolean} reduced - Whether the reduced-motion preference is set.
   * @returns {string} Trimmed computed value.
   */
  const rootMotionToken = (name, reduced) => {
    const key = `${name}|${reduced}`;
    if (!rootMotionTokens.has(key)) {
      rootMotionTokens.set(
        key,
        getComputedStyle(document.documentElement).getPropertyValue(name).trim()
      );
    }
    return rootMotionTokens.get(key);
  };

  class DieuxProductMediaGallery extends HTMLElement {
    connectedCallback() {
      this.disconnectOwner();
      this.abortController = new AbortController();
      this.viewport = this.querySelector('[data-product-media-viewport]');
      this.track = this.querySelector('[data-product-media-track]');
      this.slideCache = this.querySelector('[data-product-media-slide-cache]');
      this.thumbsShell = this.querySelector('[data-product-media-thumbs-shell]');
      this.thumbsViewport = this.querySelector('[data-product-media-thumbs]');
      this.thumbCache = this.querySelector('[data-product-media-thumb-cache]');
      this.alternateButton = this.querySelector('[data-product-media-alternate]');
      this.alternateImage = this.querySelector('.product-media-gallery__alternate-image');
      this.alternateImageWidths = this.candidateWidths(this.alternateImage);
      this.featuredFigure = this.querySelector('[data-product-media-featured-alternate]');
      this.featuredImage = this.querySelector('.product-media-gallery__featured-alternate-image');
      this.featuredImageWidths = this.candidateWidths(this.featuredImage);
      this.featuredCaption = this.querySelector('[data-product-media-featured-caption]');
      this.staticFigure = this.querySelector('[data-product-media-static]');
      this.staticImage = this.querySelector('.product-media-gallery__static-image');
      this.staticImageWidths = this.candidateWidths(this.staticImage);
      this.staticCaption = this.querySelector('[data-product-media-static-caption]');
      this.leadSlide = this.querySelector('[data-product-media-lead]');
      this.leadImage = this.querySelector('.product-media-gallery__lead-image');
      this.leadImageWidths = this.candidateWidths(this.leadImage);
      this.leadCaption = this.querySelector('[data-product-media-lead-caption]');
      this.leadThumb = this.querySelector('[data-product-media-lead-thumb]');
      this.leadThumbImage = this.leadThumb?.querySelector('img') || null;
      this.leadThumbImageWidths = this.candidateWidths(this.leadThumbImage);
      this.status = this.querySelector('[data-product-media-status]');
      this.motionPreference = (window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)'));
      this.staticLayoutPreference = window.matchMedia(
        '(width > 60rem) and (width >= calc(100svh + 43.75rem))'
      );
      this.alternateBesideLayoutPreference = window.matchMedia(
        '(width > 60rem) and (width >= calc(100svh + 31.25rem))'
      );

      const { signal } = this.abortController;
      this.videoAutoplayArmed = false;
      this.deferredMediaHydrated = false;
      this.addEventListener('click', this.armVideoAutoplay, { capture: true, signal });
      this.addEventListener('keydown', this.armVideoAutoplay, { capture: true, signal });

      for (const type of ['pointerenter', 'pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin']) {
        this.addEventListener(type, this.hydrateDeferredMedia, {
          capture: true,
          once: true,
          passive: true,
          signal,
        });
      }
      this.addEventListener('click', this.handleClick, { capture: true, signal });
      this.viewport?.addEventListener('keydown', this.handleKeydown, { signal });
      this.viewport?.addEventListener('scroll', this.handleViewportScroll, {
        passive: true,
        signal,
      });
      this.thumbsViewport?.addEventListener('scroll', this.updateThumbScrollState, {
        passive: true,
        signal,
      });
      this.thumbsViewport?.addEventListener('wheel', this.releaseThumbScroll, {
        passive: true,
        signal,
      });
      this.thumbsViewport?.addEventListener('touchmove', this.releaseThumbScroll, {
        passive: true,
        signal,
      });
      this.thumbsViewport?.addEventListener('pointerover', this.handleThumbIntent, {
        passive: true,
        signal,
      });
      this.thumbsViewport?.addEventListener('focusin', this.handleThumbIntent, { signal });
      this.variantEventOwner = this.closest(
        'product-component[data-product-grid-content][data-template-product-state-owner]'
      );
      this.variantEventOwner?.addEventListener(
        'dieux:product-variant-change',
        this.handleVariantChange,
        { signal }
      );
      window.addEventListener('dieux:swiper:ready', this.mountCarousel, { signal });
      window.addEventListener('pageshow', this.refreshBadgeState, { signal });
      document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
      this.motionPreference.addEventListener('change', this.handleMotionPreference, { signal });
      this.staticLayoutPreference.addEventListener(
        'change',
        this.handleStaticLayoutPreference,
        { signal }
      );
      if (this.leadSlide) {
        this.alternateBesideLayoutPreference.addEventListener(
          'change',
          this.handleAlternateBesideLayoutPreference,
          { signal }
        );
      }

      this.thumbResizeObserver = new ResizeObserver(this.updateThumbScrollState);
      if (this.thumbsViewport) this.thumbResizeObserver.observe(this.thumbsViewport);

      this.applyVariant(this.dataset.currentVariantId || '', false);
      this.warmFeaturedAfterEagerImages();
      this.prefetchVariantMedia();

      window.DieuxFrame?.read(() => {
        const reduced = this.motionPreference.matches;
        if (!reduced) rootMotionToken('--motion-duration-crossfade', false);
        rootMotionToken('--motion-easing', reduced);
      });
    }

    disconnectedCallback() {
      this.disconnectOwner();
    }

    disconnectOwner() {
      this.cancelTransition();
      this.resetFeaturedMedia(false);
      this.abortController?.abort();
      this.thumbResizeObserver?.disconnect();
      this.carouselController?.destroy();
      this.carouselController = null;
      this.pauseAllVideos();
      this.releaseThumbScroll();
      if (this.thumbAlignKey) window.DieuxFrame.cancelMeasure(this.thumbAlignKey);
      if (this.thumbScrollStateKey) window.DieuxFrame.cancelMeasure(this.thumbScrollStateKey);
      if (this.viewportStopKey) window.DieuxFrame.cancelMeasure(this.viewportStopKey);
      if (this.reconfigureFrame) window.cancelAnimationFrame(this.reconfigureFrame);
      if (this.viewportScrollFrame) window.cancelAnimationFrame(this.viewportScrollFrame);
      this.reconfigureFrame = undefined;
      this.viewportScrollFrame = undefined;
    }

    allSlides() {
      return Array.from(this.querySelectorAll('[data-product-media-slide]'));
    }

    allThumbs() {
      return Array.from(this.querySelectorAll('[data-product-media-thumb]'));
    }

    leadSlideEnabled() {
      return this.leadSlide instanceof HTMLElement && !this.alternateBesideLayoutPreference.matches;
    }

    leadSlideActive() {
      return this.leadSlide instanceof HTMLElement && this.activeSlides?.[0] === this.leadSlide;
    }

    mediaMatchesVariant(element, variantId) {
      if (!variantId) return false;
      return (element.dataset.mediaVariantIds || '').includes(`,${variantId},`);
    }

    mediaShouldRender(element, variantId) {
      if (this.dataset.hideNonselectedVariantImages !== 'true') return true;

      const associated = element.dataset.variantAssociated === 'true';
      return this.mediaMatchesVariant(element, variantId) || !associated;
    }

    resolveFeaturedAlternate(slides, variantId) {
      const variantImage = slides.find(
        (slide) =>
          slide.dataset.mediaType === 'image'
          && this.mediaMatchesVariant(slide, variantId)
      );
      const defaultId = String(this.dataset.defaultFeaturedAlternateId || '');
      const defaultImage = slides.find(
        (slide) =>
          slide.dataset.mediaType === 'image'
          && slide.dataset.mediaId === defaultId
      );
      return (
        variantImage
        || defaultImage
        || slides.find((slide) => slide.dataset.mediaType === 'image')
        || null
      );
    }

    applyVariant(variantId, announce = false, preserveTransition = false) {
      if (!this.track || !this.slideCache || !this.thumbsViewport || !this.thumbCache) return;

      if (!preserveTransition) this.cancelTransition();
      this.resetFeaturedMedia(false);
      this.pauseAllVideos();
      this.carouselController?.destroy();
      this.carouselController = null;

      const resolvedVariantId = variantId ? String(variantId) : '';
      const slides = this.allSlides().sort(
        (left, right) => Number(left.dataset.mediaPosition) - Number(right.dataset.mediaPosition)
      );
      const featuredAlternate = this.resolveFeaturedAlternate(slides, resolvedVariantId);
      slides.forEach((slide) => {
        slide.dataset.featuredAlternate = String(slide === featuredAlternate);
      });

      const gallerySlides = slides.filter((slide) => slide !== featuredAlternate);
      let activeSlides = gallerySlides.filter((slide) =>
        this.mediaShouldRender(slide, resolvedVariantId)
      );

      if (activeSlides.length === 0) {
        if (featuredAlternate) {
          activeSlides = [featuredAlternate];
        } else if (gallerySlides.length > 0) {
          activeSlides = [gallerySlides[0]];
        } else if (slides.length > 0) {
          activeSlides = [slides[0]];
        }
      }

      activeSlides.sort(
        (left, right) => Number(left.dataset.mediaPosition) - Number(right.dataset.mediaPosition)
      );

      if (
        this.leadSlideEnabled()
        && featuredAlternate
        && activeSlides.some((slide) => slide !== featuredAlternate)
      ) {
        activeSlides.unshift(this.leadSlide);
      }

      const activeSlideSet = new Set(activeSlides);
      const inactiveSlides = [...slides, this.leadSlide].filter(
        (slide) => slide instanceof HTMLElement && !activeSlideSet.has(slide)
      );
      const activeIds = new Set(activeSlides.map((slide) => slide.dataset.mediaId));
      const focusedSlide = document.activeElement?.closest?.('[data-product-media-slide]');
      const focusedThumb = document.activeElement?.closest?.('[data-product-media-thumb]');

      if (focusedSlide && !activeSlideSet.has(focusedSlide)) {
        this.viewport?.focus?.({ preventScroll: true });
      }

      activeSlides.forEach((slide) => {
        slide.hidden = false;
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
        this.track.append(slide);
      });
      inactiveSlides.forEach((slide) => {
        slide.hidden = true;
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
        this.slideCache.append(slide);
      });

      const thumbs = this.allThumbs().sort(
        (left, right) => Number(left.dataset.mediaPosition) - Number(right.dataset.mediaPosition)
      );
      const thumbByMediaId = new Map(thumbs.map((thumb) => [thumb.dataset.mediaId, thumb]));
      const activeThumbs = activeSlides
        .map((slide) => thumbByMediaId.get(slide.dataset.mediaId))
        .filter(
          (thumb) =>
            thumb instanceof HTMLElement
            && thumb.dataset.mediaId !== featuredAlternate?.dataset.mediaId
        );

      if (
        focusedThumb instanceof HTMLElement
        && !activeThumbs.includes(focusedThumb)
      ) {
        const focusFallback = activeThumbs[0] || this.viewport;
        focusFallback?.focus?.({ preventScroll: true });
      }

      activeThumbs.forEach((thumb) => {
        thumb.hidden = false;
        thumb.removeAttribute('aria-disabled');
        this.thumbsViewport.append(thumb);
      });
      thumbs.forEach((thumb) => {
        if (
          activeIds.has(thumb.dataset.mediaId)
          && thumb.dataset.mediaId !== featuredAlternate?.dataset.mediaId
        ) {
          return;
        }
        thumb.hidden = true;
        thumb.setAttribute('aria-disabled', 'true');
        thumb.setAttribute('aria-current', 'false');
        this.thumbCache.append(thumb);
      });

      this.activeSlides = activeSlides;
      this.activeThumbs = activeThumbs;
      this.featuredAlternateSlide = featuredAlternate;
      this.dataset.currentVariantId = resolvedVariantId;
      const selectedVariantSlide = activeSlides.find((slide) =>
        this.mediaMatchesVariant(slide, resolvedVariantId)
      );
      const activeMediaId =
        selectedVariantSlide?.dataset.mediaId
        || activeSlides[0]?.dataset.mediaId
        || '';
      this.dataset.activeMediaId = activeMediaId;
      this.viewport.scrollLeft = 0;
      this.releaseThumbScroll();
      this.thumbsViewport.scrollLeft = 0;
      const showThumbnailRail = activeThumbs.length > 1;
      this.thumbsShell.hidden = !showThumbnailRail;
      this.dataset.thumbnailRail = String(showThumbnailRail);
      this.updateFeaturedAlternate(featuredAlternate);
      this.updateFeaturedAvailability();
      this.updateThumbCurrent(activeMediaId);
      this.refreshBadgeState();
      this.preloadNextSlide(activeMediaId);
      if (!announce && this.status) this.status.textContent = '';

      if (this.reconfigureFrame) window.cancelAnimationFrame(this.reconfigureFrame);
      this.reconfigureFrame = window.requestAnimationFrame(() => {
        this.reconfigureFrame = undefined;
        if (!this.isConnected) return;
        this.viewport.scrollLeft = 0;
        this.alignCurrentThumb(activeMediaId);
        this.updateThumbScrollState();
        this.mountCarousel();
        this.syncActiveVideo(this.getActiveSlide());
      });
    }

    mountCarousel = () => {
      if (
        this.carouselController
        || !window.DieuxSwiper
        || !this.viewport
        || !this.track
        || this.activeSlides?.length === 0
      ) {
        return;
      }

      this.carouselController = window.DieuxSwiper.mount({
        owner: this,
        viewport: this.viewport,
        track: this.track,
        stateTarget: this,
        previousButton: this.querySelector('[data-swiper-button="previous"]'),
        nextButton: this.querySelector('[data-swiper-button="next"]'),
        unavailableControls: 'disabled',
        requiresOverflow: false,
        options: () => {
          const initialSlide = Math.max(
            0,
            this.activeSlides.findIndex(
              (slide) => slide.dataset.mediaId === this.dataset.activeMediaId
            )
          );

          return {
            initialSlide,
            slidesPerView: 'auto',
            loop: this.activeSlides.length > 1,
            a11y: {
              enabled: false,
            },
            on: {
              init: (swiper) => this.syncFromSwiper(swiper, false),
              slideChange: (swiper) => {
                if (swiper.slides?.[swiper.activeIndex]?.dataset.mediaId === this.dataset.activeMediaId) return;
                this.syncFromSwiper(swiper, true);
              },
              sliderFirstMove: this.handlePhysicalDragStart,
            },
          };
        },
        onStateChange: ({ state, controller }) => {
          if (state === 'enhanced' && controller.instance) {
            this.viewport.removeAttribute('tabindex');
            this.syncFromSwiper(controller.instance, false);
          } else if (state !== 'pending') {
            this.clearEnhancedAccessibility();
            this.syncStaticViewportStop();
          }
        },
      });
    };

    handleVariantChange = (event) => {
      if (String(event.detail?.productId || '') !== String(this.dataset.productId || '')) return;
      const nextVariantId = String(event.detail?.variant?.id || '');
      if (nextVariantId && nextVariantId !== String(this.dataset.currentVariantId || '')) {
        this.armVideoAutoplay();
        this.hydrateDeferredMedia();
      }
      this.transitionVariant(event.detail?.variant?.id || '');
    };

    handleClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const thumbScrollButton = event.target.closest('[data-product-media-thumb-scroll]');
      if (thumbScrollButton && this.contains(thumbScrollButton)) {
        event.preventDefault();
        if (
          this.hasAttribute('data-media-transitioning')
          && this.pendingCrossfadeIndex === undefined
        ) {
          return;
        }

        const direction = thumbScrollButton.dataset.productMediaThumbScroll;
        const delta = direction === 'previous' ? -1 : 1;
        const targetSlide = this.activeSlides?.[
          this.normalizeIndex(this.targetIndex() + delta)
        ];
        if (!(targetSlide instanceof HTMLElement)) return;

        this.scrollThumbnails(direction);
        this.showMedia(targetSlide.dataset.mediaId, true);
        return;
      }

      const alternateButton = event.target.closest('[data-product-media-alternate]');
      if (alternateButton && this.contains(alternateButton)) {
        event.preventDefault();
        this.swapFeaturedMedia(alternateButton);
        return;
      }

      const thumb = event.target.closest('[data-product-media-thumb]');
      if (thumb instanceof HTMLElement && this.contains(thumb) && !thumb.hidden) {
        event.preventDefault();
        this.showMedia(thumb.dataset.mediaId, true);
        return;
      }

      const navigationButton = event.target.closest('[data-swiper-button]');
      if (
        !(navigationButton instanceof HTMLButtonElement)
        || !this.contains(navigationButton)
        || navigationButton.disabled
      ) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      const delta = navigationButton.dataset.swiperButton === 'previous' ? -1 : 1;
      this.crossfadeTo(this.normalizeIndex(this.targetIndex() + delta), true);
    };

    handleKeydown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (
        event.target instanceof Element
        && event.target.closest(SLIDE_CONTROLS)
      ) {
        return;
      }
      const instance = this.carouselController?.instance;
      if (!instance || instance.destroyed) return;

      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -1 : 1;
      this.crossfadeTo(this.normalizeIndex(this.targetIndex() + delta), true);
    };

    targetIndex() {
      return this.pendingCrossfadeIndex ?? this.currentIndex();
    }

    currentIndex() {
      const instance = this.carouselController?.instance;
      if (instance && !instance.destroyed) return instance.realIndex ?? instance.activeIndex ?? 0;
      return Math.max(
        0,
        this.activeSlides?.findIndex(
          (slide) => slide.dataset.mediaId === this.dataset.activeMediaId
        ) ?? 0
      );
    }

    normalizeIndex(index) {
      const length = this.activeSlides?.length || 0;
      if (length === 0) return 0;
      return (index + length) % length;
    }

    preloadNextSlide(mediaId) {
      if (!mediaId || (this.activeSlides?.length || 0) < 2) return;

      const activeIndex = this.activeSlides.findIndex(
        (slide) => slide.dataset.mediaId === String(mediaId)
      );
      if (activeIndex < 0) return;

      const nextSlide = this.activeSlides[this.normalizeIndex(activeIndex + 1)];
      if (!(nextSlide instanceof HTMLElement)) return;

      nextSlide.querySelectorAll('img').forEach((image) => {
        if (!(image instanceof HTMLImageElement)) return;

        if (image.fetchPriority !== 'high') image.fetchPriority = 'low';
        image.loading = 'eager';
      });
    }

    showMedia(mediaId, announce) {
      const index = this.activeSlides?.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0) return;

      const instance = this.carouselController?.instance;
      if (instance && !instance.destroyed) {
        if (index === this.currentIndex() && this.pendingCrossfadeIndex === undefined) {
          if (announce) this.announceActiveMedia();
        } else {
          this.crossfadeTo(index, announce);
        }
        return;
      }

      const slide = this.activeSlides[index];
      this.resetFeaturedMedia(false);
      this.syncActiveVideo(slide);
      this.viewport?.scrollTo({
        left: slide.offsetLeft,
        behavior:
          this.motionPreference?.matches || this.dataset.swiperState === 'pending'
            ? 'auto'
            : 'smooth',
      });
      this.syncFallbackState(String(mediaId), announce);
    }

    handleViewportScroll = () => {
      if (this.carouselController?.instance || this.viewportScrollFrame) return;

      this.viewportScrollFrame = window.requestAnimationFrame(() => {
        this.viewportScrollFrame = undefined;
        if (this.carouselController?.instance || !this.viewport || !this.activeSlides?.length) return;

        const scrollPosition = this.viewport.scrollLeft;
        const activeSlide = this.activeSlides.reduce((closest, slide) => {
          if (!closest) return slide;
          const closestDistance = Math.abs(closest.offsetLeft - scrollPosition);
          const slideDistance = Math.abs(slide.offsetLeft - scrollPosition);
          return slideDistance < closestDistance ? slide : closest;
        }, null);

        if (!(activeSlide instanceof HTMLElement)) return;
        if (activeSlide.dataset.mediaId === this.dataset.activeMediaId) return;
        this.armVideoAutoplay();
        this.cancelTransition();
        this.resetFeaturedMedia(false);
        this.syncActiveVideo(activeSlide);
        this.syncFallbackState(activeSlide.dataset.mediaId || '', false);
      });
    };

    syncFromSwiper(swiper, announce) {
      const activeSlide = swiper.slides?.[swiper.activeIndex];
      if (!(activeSlide instanceof HTMLElement)) return;

      const preserveAlternatePlacement = this.dataset.mediaSwapped === 'true';
      if (!this.hasAttribute('data-media-transitioning')) {
        this.cancelTransition();
        if (!preserveAlternatePlacement) {
          this.resetFeaturedMedia(false);
        }
      }
      this.syncActiveVideo(activeSlide);
      const focusedSlide = document.activeElement?.closest?.('[data-product-media-slide]');
      if (focusedSlide instanceof HTMLElement && focusedSlide !== activeSlide) {
        this.viewport?.focus?.({ preventScroll: true });
      }
      swiper.slides.forEach((slide) => {
        const isActive = slide === activeSlide;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.inert = !isActive;
      });
      if (preserveAlternatePlacement) {
        this.setFeaturedMediaSwapped(true, false);
      }

      this.syncFallbackState(activeSlide.dataset.mediaId || '', announce);
    }

    syncFallbackState(mediaId, announce) {
      if (!mediaId) return;
      this.dataset.activeMediaId = mediaId;
      this.updateThumbCurrent(mediaId);
      this.alignCurrentThumb(mediaId, { smooth: announce });
      this.updateThumbScrollState();
      this.preloadNextSlide(mediaId);
      this.warmActiveAlternate();

      if (announce && !this.hasAttribute('data-media-transitioning')) {
        this.announceActiveMedia();
      }
    }

    syncStaticViewportStop() {
      const viewport = this.viewport;
      if (!(viewport instanceof HTMLElement)) return;

      const frame = window.DieuxFrame;
      this.viewportStopKey ??= Symbol('product-media-viewport-stop');
      frame.syncTabStops?.(this.viewportStopKey, {
        targets: () => [viewport],
        needsStop: (target) =>
          target.scrollWidth > target.clientWidth + 1 && !frame.hasTabbable(target, { visible: true }),
        isCurrent: () => this.isConnected && this.dataset.swiperState !== 'enhanced',
      });
    }

    clearEnhancedAccessibility() {
      this.activeSlides?.forEach((slide) => {
        const isCovered = slide === this.coveredSlide && this.dataset.mediaSwapped === 'true';
        if (isCovered) {
          slide.setAttribute('aria-hidden', 'true');
          slide.inert = true;
        } else {
          slide.removeAttribute('aria-hidden');
          slide.inert = false;
        }
      });
    }

    updateThumbCurrent(mediaId) {
      this.activeThumbs?.forEach((thumb) => {
        thumb.setAttribute('aria-current', String(thumb.dataset.mediaId === mediaId));
      });
    }

    alignCurrentThumb(mediaId, options = {}) {
      if (!this.thumbsViewport) return;

      this.thumbAlignKey ??= Symbol('product-media-thumb-align');
      window.DieuxFrame.measure(this.thumbAlignKey, () => {
        if (this.isConnected) this.measureThumbAlignment(mediaId, options);
      });
    }

    measureThumbAlignment(mediaId, options = {}) {
      if (!this.thumbsViewport) return;

      const currentThumb = this.activeThumbs?.find(
        (thumb) => thumb.dataset.mediaId === String(mediaId)
      );
      if (!(currentThumb instanceof HTMLElement)) return;

      const viewport = this.thumbsViewport;
      const viewportStyle = getComputedStyle(viewport);
      const contentLeft = viewport.getBoundingClientRect().left + viewport.clientLeft;
      const visibleStart = contentLeft + (Number.parseFloat(viewportStyle.paddingLeft) || 0);
      const visibleEnd =
        contentLeft + viewport.clientWidth - (Number.parseFloat(viewportStyle.paddingRight) || 0);
      const fadeSize = this.thumbsShell
        ? Number.parseFloat(getComputedStyle(this.thumbsShell, '::after').width) || 0
        : 0;
      const fadeStart = this.thumbsShell
        ? this.thumbsShell.getBoundingClientRect().right - fadeSize
        : visibleEnd;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const navigationClearance = options.clearNavigation ? this.thumbNavigationClearance() : null;
      const thumbBounds = currentThumb.getBoundingClientRect();
      const destination = this.thumbScrollDestination();
      const pendingShift = destination - viewport.scrollLeft;
      const thumbStart = thumbBounds.left - pendingShift;
      const thumbEnd = thumbBounds.right - pendingShift;
      const clearStart = Math.max(visibleStart, navigationClearance?.start ?? visibleStart);
      const clearEnd = Math.min(visibleEnd, fadeStart, navigationClearance?.end ?? visibleEnd);
      let target = destination;
      if (thumbStart < clearStart) {
        target = destination + thumbStart - clearStart;
        if (navigationClearance && target <= clearStart - visibleStart) target = 0;
      } else if (thumbEnd > clearEnd) {
        target = destination + thumbEnd - clearEnd;
        if (maxScroll - target <= Math.max(fadeSize, visibleEnd - clearEnd)) target = maxScroll;
      }
      if (Math.abs(target - destination) < 1) return;

      this.scrollThumbsTo(target, options.smooth === true);
    }

    thumbNavigationClearance() {
      const edge = (direction, side) => {
        const button = this.querySelector(`[data-product-media-thumb-scroll="${direction}"]`);
        if (!(button instanceof HTMLElement) || button.hidden) return undefined;
        const bounds = button.getBoundingClientRect();
        return bounds.width > 0 ? bounds[side] : undefined;
      };
      return { start: edge('previous', 'right'), end: edge('next', 'left') };
    }

    updateFeaturedAlternate(featuredSlide) {
      const sourceImage = featuredSlide?.querySelector('.product-media-gallery__media img');
      if (
        !(featuredSlide instanceof HTMLElement)
        || !(sourceImage instanceof HTMLImageElement)
      ) {
        this.dataset.featuredAlternateId = '';
        return;
      }

      const description = featuredSlide.dataset.mediaDescription || '';
      const caption = featuredSlide.dataset.mediaCaption || '';
      const openLabel = this.dataset.openAlternateLabel || '';
      const showAlternateLabel =
        openLabel && description ? `${openLabel}: ${description}` : description || openLabel;
      this.dataset.featuredAlternateId = featuredSlide.dataset.mediaId || '';
      this.dataset.showAlternateLabel = showAlternateLabel;
      this.copyPreviewImage(this.featuredImage, sourceImage, description, this.featuredImageWidths);
      this.copyPreviewImage(this.alternateImage, sourceImage, '', this.alternateImageWidths);
      this.copyPreviewImage(this.staticImage, sourceImage, description, this.staticImageWidths);
      this.syncCaption(this.featuredCaption, caption);
      this.syncCaption(this.staticCaption, caption);
      if (this.leadSlide) {
        this.copyPreviewImage(this.leadImage, sourceImage, description, this.leadImageWidths);
        this.copyPreviewImage(this.leadThumbImage, sourceImage, '', this.leadThumbImageWidths);
        this.syncCaption(this.leadCaption, caption);
        this.leadSlide.dataset.mediaDescription = description;
        this.leadSlide.dataset.mediaCaption = caption;
        this.leadThumb?.setAttribute(
          'aria-label',
          (this.leadThumb.dataset.labelTemplate || '[title]').replace('[title]', () => description)
        );
        const featuredPosition = featuredSlide.dataset.mediaPosition || '';
        if (this.leadThumb && /^\d+$/.test(featuredPosition)) {
          const leadLinkName = String(Number(featuredPosition) + 1);
          if (this.leadThumb.dataset.clickLinkName !== leadLinkName) {
            this.leadThumb.dataset.clickLinkName = leadLinkName;
          }
        }
      }
      this.alternateButton?.setAttribute(
        'aria-label',
        showAlternateLabel
      );
    }

    copyPreviewImage(target, source, alt, widths) {
      if (!(target instanceof HTMLImageElement) || !(source instanceof HTMLImageElement)) return;
      const rescaled = widths?.length ? this.rescaledPreviewSource(source, widths) : null;
      if (rescaled) {
        target.srcset = rescaled.srcset;
        target.src = rescaled.src;
      } else {
        if (source.srcset) {
          target.srcset = source.srcset;
        } else {
          target.removeAttribute('srcset');
        }
        target.src = source.src;
      }
      target.alt = alt;
      target.style.objectPosition = getComputedStyle(source).objectPosition;
    }

    candidateWidths(image) {
      if (!(image instanceof HTMLImageElement)) return [];
      const widths = [];
      for (const candidate of (image.getAttribute('srcset') || '').split(',')) {
        const width = Number((candidate.trim().match(/\s(\d+)w$/) || [])[1]);
        if (width > 0) widths.push(width);
      }
      return widths;
    }

    rescaledPreviewSource(source, widths) {
      let url;
      try {
        url = new URL(source.src || source.currentSrc, document.baseURI);
      } catch {
        return null;
      }
      if (!url.searchParams.has('width')) return null;

      const candidates = widths.map((width) => {
        url.searchParams.set('width', String(width));
        return `${url.href} ${width}w`;
      });
      url.searchParams.set('width', String(Math.max(...widths)));
      return { srcset: candidates.join(', '), src: url.href };
    }

    syncCaption(target, caption) {
      if (!(target instanceof HTMLElement)) return;
      target.textContent = caption;
      target.hidden = caption === '';
    }

    updateFeaturedAvailability() {
      const hasAlternate =
        this.featuredAlternateSlide instanceof HTMLElement
        && this.activeSlides?.some((slide) => slide !== this.featuredAlternateSlide);
      const alternateOwnedFocus =
        this.alternateButton instanceof HTMLElement
        && this.alternateButton.contains(document.activeElement);

      const showAlternate = hasAlternate && !this.leadSlideActive();

      this.dataset.featuredAlternateAvailable = String(hasAlternate);
      if (this.featuredFigure) this.featuredFigure.hidden = !showAlternate;
      if (this.alternateButton instanceof HTMLButtonElement) {
        this.alternateButton.hidden = !showAlternate;
        this.alternateButton.disabled = !showAlternate;
      }
      if (this.staticFigure) this.staticFigure.hidden = !hasAlternate;
      if (!showAlternate) this.resetFeaturedMedia(false);
      if (!showAlternate && alternateOwnedFocus) {
        this.viewport?.focus?.({ preventScroll: true });
      }
    }

    warmFeaturedAfterEagerImages() {
      const gate = this.featuredImage?.parentElement?.querySelector('[data-product-media-deferred-source]');
      if (!gate) return;
      const { signal } = this.abortController;
      const images = [...this.querySelectorAll('img[loading="eager"]')].filter(
        (image) => image !== this.featuredImage
      );
      const release = () => {
        if (signal.aborted || images.some((image) => this.contains(image) && !image.complete)) return;
        this.removeEventListener('load', release, true);
        this.removeEventListener('error', release, true);
        gate.remove();
      };
      this.addEventListener('load', release, { capture: true, signal });
      this.addEventListener('error', release, { capture: true, signal });
      release();
    }

    /**
     * Warm the images a variant change or featured swap will need, so `primeCrossfadeImages` opens immediately instead of holding the crossfade for a download.
     */
    prefetchVariantMedia() {
      if (this.variantMediaPrefetched) return;
      this.variantMediaPrefetched = true;
      loadMediaSwap()
        .then(({ prefetchSwapImages }) => {
          if (!this.isConnected) return;
          const images = [];
          if (
            this.featuredImage instanceof HTMLImageElement
            && !this.featuredFigure?.hidden
            && !this.previewTargetGated(this.featuredImage)
          ) {
            images.push(this.featuredImage);
          }
          images.push(
            ...this.neighbourSlideImages(1, 0).map((image) =>
              this.previewTargetGated(image) ? this.inertCopies([image])[0] : image
            )
          );
          const activeImage = this.slidePreviewImage(this.getActiveSlide());
          if (activeImage) images.push(...this.previewWarmCarriers(activeImage, true));
          for (const slide of this.allSlides()) {
            if (slide.dataset.variantAssociated !== 'true') continue;
            const slideImage = this.slidePreviewImage(slide);
            if (slideImage) images.push(slideImage, ...this.previewWarmCarriers(slideImage));
          }
          for (const thumb of this.allThumbs()) {
            if (!thumb.hidden || thumb === this.leadThumb) continue;
            const thumbImage = thumb.querySelector('img');
            if (thumbImage instanceof HTMLImageElement) images.push(thumbImage);
          }
          if (images.length) prefetchSwapImages(images);
        })
        .catch(() => {});
    }

    /**
     * @param {number} after - Slides after the active one to include.
     * @param {number} before - Slides before the active one to include.
     * @returns {HTMLImageElement[]} Preview images of the neighbouring slides of the current variant.
     */
    neighbourSlideImages(after, before) {
      const slides = this.activeSlides || [];
      if (slides.length < 2) return [];
      const activeIndex = Math.max(
        0,
        slides.findIndex((slide) => slide.dataset.mediaId === this.dataset.activeMediaId)
      );
      const picked = new Set();
      for (let step = 1; step <= after; step += 1) picked.add(slides[this.normalizeIndex(activeIndex + step)]);
      for (let step = 1; step <= before; step += 1) picked.add(slides[this.normalizeIndex(activeIndex - step)]);
      picked.delete(slides[activeIndex]);
      return [...picked].map((slide) => this.slidePreviewImage(slide)).filter(Boolean);
    }

    /**
     * @returns {HTMLImageElement[]} Every current-variant slide image, nearest first, then each shared
     *   slide's alternate carrier.
     */
    engagedGalleryImages() {
      const slides = this.activeSlides || [];
      const images = this.neighbourSlideImages(2, 1);
      images.push(...this.neighbourSlideImages(slides.length, 0));
      for (const slide of this.allSlides()) {
        if (slide.dataset.variantAssociated === 'true') continue;
        const slideImage = this.slidePreviewImage(slide);
        if (slideImage) images.push(...this.previewWarmCarriers(slideImage, true));
      }
      return this.inertCopies([...new Set(images)]);
    }

    /**
     * Inert copies of live slide images, so a warm fills the cache without loading the carousel's own images.
     *
     * @param {HTMLImageElement[]} images - Slide or carrier images.
     * @returns {HTMLImageElement[]} The same list with every connected image replaced by an inert copy.
     */
    inertCopies(images) {
      const carrier = document.createElement('template');
      return images.map((image) => {
        if (!image.isConnected) return image;
        const copy = document.createElement('img');
        carrier.content.append(copy);
        for (const name of ['sizes', 'srcset', 'src']) {
          const value = image.getAttribute(name);
          if (value) copy.setAttribute(name, value);
        }
        return copy;
      });
    }

    /** Warm the alternate control's copy of the active slide, a URL nothing else requests. */
    warmActiveAlternate() {
      const image = this.slidePreviewImage(this.getActiveSlide());
      if (!image) return;
      loadMediaSwap()
        .then(({ prefetchSwapImages }) => {
          if (!this.isConnected) return;
          prefetchSwapImages(this.previewWarmCarriers(image, true), { intent: document.readyState === 'complete' });
        })
        .catch(() => {});
    }

    handleThumbIntent = (event) => {
      const thumb = event.target instanceof Element ? event.target.closest('[data-product-media-thumb]') : null;
      if (!(thumb instanceof HTMLElement) || thumb.hidden) return;
      if (event.type === 'focusin' && document.documentElement.dataset.focusModality === 'keyboard') {
        this.alignCurrentThumb(thumb.dataset.mediaId, { clearNavigation: true });
      }
      const slide = this.activeSlides?.find((candidate) => candidate.dataset.mediaId === thumb.dataset.mediaId);
      const image = slide ? this.slidePreviewImage(slide) : null;
      if (!image) return;
      loadMediaSwap()
        .then(({ prefetchSwapImages }) => {
          prefetchSwapImages(this.inertCopies([image]), { intent: document.readyState === 'complete' });
        })
        .catch(() => {});
    };

    /**
     * The slide's preview image, read from its deferred template while the slide is still unhydrated.
     * @param {HTMLElement} slide
     * @returns {HTMLImageElement | null}
     */
    slidePreviewImage(slide) {
      const container = slide?.querySelector('.product-media-gallery__media');
      const liveImage = container?.querySelector('img');
      if (liveImage instanceof HTMLImageElement) return liveImage;
      for (const template of container?.querySelectorAll('template') || []) {
        const image = template.content.querySelector('img');
        if (image instanceof HTMLImageElement) return image;
      }
      return null;
    }

    /**
     * inside a template's content so assigning their sources cannot start a fetch ahead of the prefetch queue.
     */
    previewWarmCarriers(slideImage, alternateOnly = false) {
      const carrier = document.createElement('template');
      const carriers = [];
      const addCarrier = (target, rescaleWidths) => {
        if (!(target instanceof HTMLImageElement) || this.previewTargetGated(target)) return;
        const rescaled = rescaleWidths?.length
          ? this.rescaledPreviewSource(slideImage, rescaleWidths)
          : null;
        const srcset = rescaled ? rescaled.srcset : slideImage.getAttribute('srcset');
        const src = rescaled ? rescaled.src : slideImage.getAttribute('src');
        if (!srcset && !src) return;
        const warm = document.createElement('img');
        carrier.content.append(warm);
        const sizes = target.getAttribute('sizes');
        if (sizes) warm.setAttribute('sizes', sizes);
        if (srcset) warm.setAttribute('srcset', srcset);
        if (src) warm.setAttribute('src', src);
        carriers.push(warm);
      };
      if (!alternateOnly) {
        if (this.dataset.mediaSwapped === 'true') addCarrier(this.featuredImage);
        addCarrier(this.staticImage);
      }
      addCarrier(this.alternateImage, this.alternateImageWidths);
      return carriers;
    }

    /**
     * @param {HTMLImageElement} target - Featured, static, or alternate preview image.
     * @returns {boolean} Whether its picture's blank source wins in the current layout.
     */
    previewTargetGated(target) {
      if (!(target.parentElement instanceof HTMLPictureElement)) return false;
      return Array.from(target.parentElement.querySelectorAll('source[media]')).some(
        (gate) => gate.media && window.matchMedia(gate.media).matches
      );
    }

    async swapFeaturedMedia(button) {
      if (
        !(button instanceof HTMLButtonElement)
        || button.disabled
        || this.hasAttribute('data-media-swapping')
        || this.hasAttribute('data-media-transitioning')
      ) {
        return;
      }

      const activeSlide = this.getActiveSlide();
      const sourceImage = activeSlide?.querySelector('.product-media-gallery__media img');
      if (!(activeSlide instanceof HTMLElement) || !(sourceImage instanceof HTMLImageElement)) return;

      this.transitionId = (this.transitionId || 0) + 1;
      const transitionId = this.transitionId;
      this.animations ||= new Set();
      this.setAttribute('data-media-swapping', '');
      const nextState = this.dataset.mediaSwapped !== 'true';
      this.pendingSwapState = nextState;
      const motionEnabled = !this.motionPreference.matches;
      const featuredReady = nextState ? this.primeCrossfadeImages([this.featuredImage]) : null;
      let outgoingAnimation;

      if (motionEnabled) {
        outgoingAnimation = button.animate(
          [
            { opacity: 1, transform: 'scale(1)' },
            { opacity: 0, transform: 'scale(1.08)' },
          ],
          {
            duration: 150,
            easing: 'ease-in',
            fill: 'both',
          }
        );
        this.animations.add(outgoingAnimation);
        await outgoingAnimation.finished.catch(() => {});
        if (transitionId !== this.transitionId) return;
      }

      if (featuredReady) {
        await featuredReady;
        if (transitionId !== this.transitionId) return;
      }

      this.setFeaturedMediaSwapped(nextState, true);

      if (motionEnabled) {
        await this.primeCrossfadeImages([this.alternateImage]);
        if (transitionId !== this.transitionId) return;
      }

      if (outgoingAnimation) {
        outgoingAnimation.cancel();
        this.animations.delete(outgoingAnimation);
      }

      if (motionEnabled) {
        const incomingAnimation = button.animate(
          [
            { opacity: 0, transform: 'scale(0.88)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          {
            duration: 300,
            easing: 'cubic-bezier(.22, 1, .36, 1)',
            fill: 'both',
          }
        );
        this.animations.add(incomingAnimation);
        await incomingAnimation.finished.catch(() => {});
        if (transitionId !== this.transitionId) return;
        incomingAnimation.cancel();
        this.animations.delete(incomingAnimation);
      }

      this.removeAttribute('data-media-swapping');
      this.pendingSwapState = undefined;
    }

    setFeaturedMediaSwapped(swapped, announce) {
      if (!(this.featuredFigure instanceof HTMLElement)) return;

      if (swapped) {
        const activeSlide = this.getActiveSlide();
        const sourceImage = activeSlide?.querySelector('.product-media-gallery__media img');
        if (!(activeSlide instanceof HTMLElement) || !(sourceImage instanceof HTMLImageElement)) return;

        this.pauseSlideVideo(activeSlide);
        this.copyPreviewImage(this.alternateImage, sourceImage, '', this.alternateImageWidths);
        this.dataset.mediaSwapped = 'true';
        this.alternateButton?.setAttribute(
          'aria-label',
          (this.dataset.restoreAlternateLabelTemplate || '[title]').replace(
            '[title]',
            activeSlide.dataset.mediaDescription || ''
          )
        );
        this.featuredFigure.setAttribute('aria-hidden', 'false');
        this.coveredSlide = activeSlide;
        activeSlide.setAttribute('aria-hidden', 'true');
        activeSlide.inert = true;
        if (announce && this.status) this.status.textContent = '';
        return;
      }

      delete this.dataset.mediaSwapped;
      this.alternateButton?.setAttribute(
        'aria-label',
        this.dataset.showAlternateLabel
          || this.featuredAlternateSlide?.dataset.mediaDescription
          || ''
      );
      this.featuredFigure.setAttribute('aria-hidden', 'true');
      const featuredSource = this.featuredAlternateSlide?.querySelector(
        '.product-media-gallery__media img'
      );
      if (featuredSource instanceof HTMLImageElement) {
        this.copyPreviewImage(this.alternateImage, featuredSource, '', this.alternateImageWidths);
      }
      this.restoreCoveredSlide();
      this.syncActiveVideo(this.getActiveSlide());
      if (announce) this.announceActiveMedia();
    }

    resetFeaturedMedia(announce = false) {
      this.pendingSwapState = undefined;
      if (this.dataset.mediaSwapped === 'true') {
        this.setFeaturedMediaSwapped(false, announce);
      } else {
        this.restoreCoveredSlide();
        this.featuredFigure?.setAttribute('aria-hidden', 'true');
      }
      this.removeAttribute('data-media-swapping');
    }

    restoreCoveredSlide() {
      if (!(this.coveredSlide instanceof HTMLElement)) return;

      const coveredSlide = this.coveredSlide;
      this.coveredSlide = null;
      const instance = this.carouselController?.instance;
      const activeSlide =
        instance && !instance.destroyed ? instance.slides?.[instance.activeIndex] : null;
      const shouldRemainHidden = activeSlide instanceof HTMLElement && coveredSlide !== activeSlide;
      if (shouldRemainHidden) {
        coveredSlide.setAttribute('aria-hidden', 'true');
        coveredSlide.inert = true;
      } else {
        coveredSlide.removeAttribute('aria-hidden');
        coveredSlide.inert = false;
      }
    }

    getActiveSlide() {
      const instance = this.carouselController?.instance;
      const swiperSlide =
        instance && !instance.destroyed ? instance.slides?.[instance.activeIndex] : null;
      if (swiperSlide instanceof HTMLElement) return swiperSlide;
      return (
        this.activeSlides?.find(
          (slide) => slide.dataset.mediaId === this.dataset.activeMediaId
        ) || this.activeSlides?.[0] || null
      );
    }

    async transitionSingleMedia(resolvedVariantId) {
      const slides = this.allSlides().sort(
        (left, right) => Number(left.dataset.mediaPosition) - Number(right.dataset.mediaPosition)
      );
      const mediaChanged =
        this.resolveFeaturedAlternate(slides, resolvedVariantId) !== this.activeSlides?.[0];

      this.cancelTransition();
      this.setAttribute('data-media-transitioning', '');
      const transitionId = this.transitionId;
      const duration = this.getCrossfadeDuration();
      const outgoingLayer = mediaChanged
        ? this.createVisualOutgoingLayer(this.getActiveSlide(), duration)
        : null;
      if (outgoingLayer) {
        this.viewport.append(outgoingLayer);
        this.outgoingLayer = outgoingLayer;
      }

      this.applyVariant(resolvedVariantId, false, true);

      if (outgoingLayer) {
        await this.primeCrossfadeImages([
          ...this.crossfadeLayerImages([outgoingLayer]),
          ...(this.activeSlides?.[0]?.querySelectorAll('img') || []),
        ]);
        if (transitionId !== this.transitionId) return;

        await this.animateCrossfadeLayers(outgoingLayer, null, duration);
        if (transitionId !== this.transitionId) return;
      }

      this.cancelTransition();
    }

    async transitionVariant(variantId) {
      const resolvedVariantId = variantId ? String(variantId) : '';
      if (resolvedVariantId === String(this.dataset.currentVariantId || '')) {
        return;
      }

      if (
        this.activeSlides?.length === 1
        && this.activeSlides[0] === this.featuredAlternateSlide
      ) {
        await this.transitionSingleMedia(resolvedVariantId);
        return;
      }

      if (this.leadSlideActive()) {
        await this.transitionLeadVariant(resolvedVariantId);
        return;
      }

      const alternateWasVisible =
        typeof this.pendingSwapState === 'boolean'
          ? this.pendingSwapState
          : this.dataset.mediaSwapped === 'true';
      this.cancelTransition();
      this.setAttribute('data-media-transitioning', '');
      const transitionId = this.transitionId;
      const duration = this.getCrossfadeDuration();
      const previousFeaturedAlternateId = String(this.dataset.featuredAlternateId || '');
      const outgoingLayer = alternateWasVisible
        ? this.createVisualOutgoingLayer(this.featuredImage, duration)
        : null;
      const swapOutgoingLayer = duration > 0 ? this.createSwapOutgoingLayer() : null;
      const staticOutgoingLayer = this.createVisualOutgoingLayer(this.staticImage, duration);

      const slides = this.allSlides().sort(
        (left, right) => Number(left.dataset.mediaPosition) - Number(right.dataset.mediaPosition)
      );
      const featuredAlternate = this.resolveFeaturedAlternate(slides, resolvedVariantId);
      slides.forEach((slide) => {
        slide.dataset.featuredAlternate = String(slide === featuredAlternate);
      });
      this.featuredAlternateSlide = featuredAlternate;
      this.dataset.currentVariantId = resolvedVariantId;
      this.updateFeaturedAlternate(featuredAlternate);
      this.updateFeaturedAvailability();
      this.refreshBadgeState();

      const preserveAlternatePlacement =
        alternateWasVisible
        && this.dataset.featuredAlternateAvailable === 'true';
      if (preserveAlternatePlacement) {
        this.setFeaturedMediaSwapped(true, false);
      }
      const alternateChanged =
        previousFeaturedAlternateId !== String(this.dataset.featuredAlternateId || '');

      if (!alternateChanged) {
        outgoingLayer?.remove();
        this.outgoingLayer = null;
        swapOutgoingLayer?.remove();
        this.swapOutgoingLayer = null;
        staticOutgoingLayer?.remove();
        this.staticOutgoingLayer = null;
      }
      if (
        alternateChanged
        && staticOutgoingLayer
        && this.staticFigure instanceof HTMLElement
      ) {
        this.staticFigure.append(staticOutgoingLayer);
        this.staticOutgoingLayer = staticOutgoingLayer;
      }

      if (outgoingLayer && alternateChanged) {
        this.viewport.append(outgoingLayer);
        this.outgoingLayer = outgoingLayer;
      }

      if (alternateChanged && duration > 0) {
        await this.primeCrossfadeImages([
          ...this.crossfadeLayerImages([outgoingLayer, swapOutgoingLayer, staticOutgoingLayer]),
          ...(preserveAlternatePlacement ? [this.featuredImage] : []),
          this.staticImage,
          this.alternateImage,
        ]);
        if (transitionId !== this.transitionId) return;

        await this.animateCrossfadeLayers(
          outgoingLayer,
          swapOutgoingLayer,
          duration,
          [staticOutgoingLayer]
        );
        if (transitionId !== this.transitionId) return;
      }

      this.cancelTransition();
      if (preserveAlternatePlacement) {
        this.setFeaturedMediaSwapped(true, false);
      }
    }

    async transitionLeadVariant(resolvedVariantId) {
      this.cancelTransition();
      this.setAttribute('data-media-transitioning', '');
      const transitionId = this.transitionId;
      const instance = this.carouselController?.instance;
      const duration = this.getCrossfadeDuration();
      const outgoingLayer = this.createVisualOutgoingLayer(this.getActiveSlide(), duration);
      if (outgoingLayer) {
        this.viewport.append(outgoingLayer);
        this.outgoingLayer = outgoingLayer;
      }

      const slides = this.allSlides().sort(
        (left, right) => Number(left.dataset.mediaPosition) - Number(right.dataset.mediaPosition)
      );
      const featuredAlternate = this.resolveFeaturedAlternate(slides, resolvedVariantId);
      slides.forEach((slide) => {
        slide.dataset.featuredAlternate = String(slide === featuredAlternate);
      });
      this.featuredAlternateSlide = featuredAlternate;
      this.dataset.currentVariantId = resolvedVariantId;
      this.updateFeaturedAlternate(featuredAlternate);
      this.updateFeaturedAvailability();
      this.refreshBadgeState();

      if (outgoingLayer) {
        await this.primeCrossfadeImages([
          ...this.crossfadeLayerImages([outgoingLayer]),
          this.leadImage,
        ]);
        if (transitionId !== this.transitionId) return;
      }

      if (instance && !instance.destroyed) {
        if (instance.params.loop) instance.slideToLoop(0, 0);
        else instance.slideTo(0, 0);
      } else if (this.viewport) {
        this.viewport.scrollLeft = 0;
        this.syncFallbackState(this.leadSlide.dataset.mediaId || '', false);
        this.syncActiveVideo(this.leadSlide);
      }

      await this.animateCrossfadeLayers(outgoingLayer, null, duration);
      if (transitionId !== this.transitionId) return;

      this.cancelTransition();
      if (instance && !instance.destroyed) this.syncFromSwiper(instance, false);
    }

    async crossfadeTo(index, announce) {
      const instance = this.carouselController?.instance;
      if (!instance || instance.destroyed) return;

      const awaitingPixels = this.pendingCrossfadeIndex !== undefined;
      if (awaitingPixels && index === this.pendingCrossfadeIndex) return;
      if (!awaitingPixels && this.hasAttribute('data-media-transitioning')) return;
      if (index === this.currentIndex()) {
        if (awaitingPixels) this.cancelTransition();
        return;
      }

      this.cancelTransition();
      this.setAttribute('data-media-transitioning', '');
      const transitionId = this.transitionId;
      const targetMediaId = this.activeSlides?.[index]?.dataset.mediaId || '';
      this.pendingCrossfadeIndex = index;
      this.updateThumbCurrent(targetMediaId);
      this.alignCurrentThumb(targetMediaId, { smooth: true });
      const duration = this.getCrossfadeDuration();
      const currentSlide = this.getActiveSlide();
      const outgoingSource =
        this.dataset.mediaSwapped === 'true' ? this.featuredImage : currentSlide;
      const outgoingLayer = this.createVisualOutgoingLayer(outgoingSource, duration);
      const swapOutgoingLayer =
        duration > 0 && this.dataset.mediaSwapped === 'true'
          ? this.createSwapOutgoingLayer()
          : null;

      this.resetFeaturedMedia(false);
      if (outgoingLayer) {
        this.viewport.append(outgoingLayer);
        this.outgoingLayer = outgoingLayer;
      }

      if (outgoingLayer) {
        const incomingImages = this.activeSlides?.[index]?.querySelectorAll('img') || [];
        await this.primeCrossfadeImages([
          ...this.crossfadeLayerImages([outgoingLayer]),
          ...incomingImages,
        ]);
        if (transitionId !== this.transitionId || instance.destroyed) return;
      }

      this.pendingCrossfadeIndex = undefined;
      if (instance.params.loop) instance.slideToLoop(index, 0);
      else instance.slideTo(index, 0);

      await this.animateCrossfadeLayers(
        outgoingLayer,
        swapOutgoingLayer,
        duration
      );
      if (transitionId !== this.transitionId) return;

      this.cancelTransition();
      this.syncFromSwiper(instance, announce);
    }

    async primeCrossfadeImages(images) {
      const pending = [];
      for (const image of images) {
        if (!(image instanceof HTMLImageElement)) continue;
        const gate = image.parentElement?.querySelector('[data-product-media-deferred-source]');
        if (gate || !image.complete) image.fetchPriority = 'high';
        if (gate) {
          gate.remove();
          await Promise.resolve();
        }
        if (image.loading === 'lazy') image.loading = 'eager';
        const decoded = image.decode?.();
        if (decoded && typeof decoded.then === 'function') {
          pending.push(decoded.catch(() => {}));
        }
      }
      if (pending.length === 0) return;
      await Promise.race([
        Promise.all(pending),
        new Promise((resolve) => window.setTimeout(resolve, CROSSFADE_READY_TIMEOUT)),
      ]);
    }

    crossfadeLayerImages(layers) {
      const images = [];
      for (const layer of layers) {
        if (layer instanceof HTMLImageElement) images.push(layer);
        else if (layer instanceof HTMLElement) images.push(...layer.querySelectorAll('img'));
      }
      return images;
    }

    async animateCrossfadeLayers(
      outgoingLayer,
      swapOutgoingLayer,
      duration,
      additionalOutgoingLayers = []
    ) {
      const animations = [];
      const outgoingLayers = [
        outgoingLayer,
        swapOutgoingLayer,
        ...additionalOutgoingLayers,
      ].filter((layer) => layer instanceof HTMLElement && layer.isConnected);

      outgoingLayers.forEach((layer) => {
        const animation = layer.animate(
          [{ opacity: 1 }, { opacity: 0 }],
          {
            duration,
            easing: this.getMotionEasing(),
            fill: 'both',
          }
        );
        this.animations.add(animation);
        animations.push(animation);
      });

      if (animations.length > 0) {
        await Promise.all(animations.map((animation) => animation.finished.catch(() => {})));
      }
    }

    createImageSnapshot(sourceImage) {
      if (!(sourceImage instanceof HTMLImageElement)) return null;
      if (!sourceImage.complete || sourceImage.naturalWidth === 0) return null;

      const bounds = sourceImage.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const widthLimit = Math.round(bounds.width * pixelRatio) || sourceImage.naturalWidth;
      const heightLimit = Math.round(bounds.height * pixelRatio) || sourceImage.naturalHeight;
      const scale = Math.min(
        1,
        widthLimit / sourceImage.naturalWidth,
        heightLimit / sourceImage.naturalHeight
      );
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sourceImage.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(sourceImage.naturalHeight * scale));
      try {
        const context = canvas.getContext('2d');
        if (!context) return null;
        context.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);
      } catch {
        return null;
      }
      return canvas;
    }

    createVisualOutgoingLayer(source, duration) {
      if (duration <= 0) return null;

      const sourceVideo = source?.querySelector?.('video');
      const sourcePlayControl = source?.querySelector?.(
        'video-player-component:not([data-video-shown]) .video-player__poster-control'
      );
      const sourceImage =
        source instanceof HTMLImageElement
          ? source
          : source?.querySelector?.('.product-media-gallery__media img');
      let visual;
      let objectPosition;
      let capturedVideoFrame = false;

      if (
        sourceVideo instanceof HTMLVideoElement
        && sourceVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
        && sourceVideo.videoWidth > 0
        && sourceVideo.videoHeight > 0
      ) {
        const canvas = document.createElement('canvas');
        const videoBounds = sourceVideo.getBoundingClientRect();
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const widthLimit = Math.max(1, Math.round(videoBounds.width * pixelRatio));
        const heightLimit = Math.max(1, Math.round(videoBounds.height * pixelRatio));
        const snapshotScale = Math.min(
          1,
          widthLimit / sourceVideo.videoWidth,
          heightLimit / sourceVideo.videoHeight
        );
        canvas.width = Math.max(1, Math.round(sourceVideo.videoWidth * snapshotScale));
        canvas.height = Math.max(1, Math.round(sourceVideo.videoHeight * snapshotScale));
        try {
          const context = canvas.getContext('2d');
          if (context) {
            context.drawImage(sourceVideo, 0, 0, canvas.width, canvas.height);
            visual = canvas;
            objectPosition = getComputedStyle(sourceVideo).objectPosition;
            capturedVideoFrame = true;
          }
        } catch {
          visual = null;
        }
      }

      if (!visual && sourceImage instanceof HTMLImageElement) {
        visual = this.createImageSnapshot(sourceImage);
        if (!visual) {
          const image = sourceImage.cloneNode(false);
          image.removeAttribute('id');
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.removeAttribute('hidden');
          image.loading = 'eager';
          image.decoding = 'sync';
          image.src = sourceImage.currentSrc || sourceImage.src;
          visual = image;
        }
        objectPosition = getComputedStyle(sourceImage).objectPosition;
      }

      if (!(visual instanceof HTMLElement)) return null;

      const layer = document.createElement('div');
      layer.className = 'product-media-gallery__crossfade-outgoing';
      layer.setAttribute('aria-hidden', 'true');
      layer.inert = true;
      if (source?.querySelector?.('.video-player__controls:not([hidden])')) {
        layer.dataset.videoControls = '';
      }
      layer.style.setProperty(
        '--product-media-object-position',
        objectPosition || 'center'
      );

      visual.className = 'product-media-gallery__crossfade-image';
      if (visual instanceof HTMLImageElement) visual.alt = '';
      visual.setAttribute('aria-hidden', 'true');
      layer.append(visual);

      if (!capturedVideoFrame) {
        if (sourcePlayControl instanceof HTMLElement) {
          const playControl = sourcePlayControl.cloneNode(true);
          playControl.className = 'product-media-gallery__crossfade-play';
          playControl.removeAttribute('id');
          playControl.setAttribute('aria-hidden', 'true');
          layer.append(playControl);
        }
      }

      const sourceFigure =
        source === this.featuredImage
          ? this.featuredFigure
          : source === this.staticImage
            ? this.staticFigure
            : null;
      const sourceCaption =
        sourceFigure?.querySelector?.('.product-media-gallery__caption:not([hidden])')
        || source?.querySelector?.('.product-media-gallery__caption:not([hidden])');
      if (
        sourceCaption instanceof HTMLElement
        && !sourceCaption.hidden
        && sourceCaption.textContent.trim() !== ''
      ) {
        const captionClone = sourceCaption.cloneNode(true);
        captionClone.removeAttribute('id');
        captionClone.setAttribute('aria-hidden', 'true');
        layer.append(captionClone);
      }

      const sourceBadges = (sourceFigure || source)?.querySelector?.(
        '.product-media-gallery__badges:not([hidden])'
      );
      if (sourceBadges instanceof HTMLElement) {
        const badgesClone = sourceBadges.cloneNode(true);
        badgesClone.removeAttribute('id');
        badgesClone.setAttribute('aria-hidden', 'true');
        layer.append(badgesClone);
      }

      return layer;
    }

    createSwapOutgoingLayer() {
      if (
        !(this.alternateButton instanceof HTMLButtonElement)
        || !(this.alternateImage instanceof HTMLImageElement)
      ) {
        return null;
      }

      let outgoingLayer = this.createImageSnapshot(this.alternateImage);
      if (outgoingLayer) {
        outgoingLayer.className = 'product-media-gallery__alternate-image';
        outgoingLayer.style.objectPosition = getComputedStyle(this.alternateImage).objectPosition;
      } else {
        outgoingLayer = this.alternateImage.cloneNode(false);
        outgoingLayer.removeAttribute('id');
        outgoingLayer.removeAttribute('srcset');
        outgoingLayer.removeAttribute('sizes');
        outgoingLayer.loading = 'eager';
        outgoingLayer.decoding = 'sync';
        outgoingLayer.src = this.alternateImage.currentSrc || this.alternateImage.src;
        outgoingLayer.alt = '';
      }
      outgoingLayer.classList.add('product-media-gallery__alternate-image--outgoing');
      outgoingLayer.setAttribute('aria-hidden', 'true');
      this.alternateButton.append(outgoingLayer);
      this.swapOutgoingLayer = outgoingLayer;
      return outgoingLayer;
    }

    cancelTransition(removeState = true) {
      this.transitionId = (this.transitionId || 0) + 1;
      if (this.pendingCrossfadeIndex !== undefined) {
        this.pendingCrossfadeIndex = undefined;
        this.updateThumbCurrent(this.dataset.activeMediaId || '');
        this.alignCurrentThumb(this.dataset.activeMediaId || '', { smooth: true });
      }
      this.animations?.forEach((animation) => animation.cancel());
      this.animations = new Set();
      this.outgoingLayer?.remove();
      this.outgoingLayer = null;
      this.swapOutgoingLayer?.remove();
      this.swapOutgoingLayer = null;
      this.staticOutgoingLayer?.remove();
      this.staticOutgoingLayer = null;
      this.pendingSwapState = undefined;
      this.removeAttribute('data-media-swapping');
      if (removeState) this.removeAttribute('data-media-transitioning');
    }

    getCrossfadeDuration() {
      if (this.motionPreference.matches) return 0;
      const raw = rootMotionToken('--motion-duration-crossfade', false);
      const value = Number.parseFloat(raw);
      if (!Number.isFinite(value)) return 150;
      if (raw.endsWith('ms')) return Math.max(0, value);
      if (raw.endsWith('s')) return Math.max(0, value * 1000);
      return 150;
    }

    getMotionEasing() {
      return (
        rootMotionToken('--motion-easing', this.motionPreference.matches)
        || 'ease-in-out'
      );
    }

    formatStatus(index, length) {
      return (this.dataset.statusTemplate || '')
        .replace('[index]', String(index))
        .replace('[length]', String(length));
    }

    announceActiveMedia() {
      if (!this.status) return;
      const activeSlide = this.getActiveSlide();
      const index = this.activeSlides?.indexOf(activeSlide) ?? -1;
      if (!(activeSlide instanceof HTMLElement) || index < 0) {
        this.status.textContent = '';
        return;
      }

      const description = activeSlide.dataset.mediaDescription || '';
      const position = this.formatStatus(index + 1, this.activeSlides.length);
      this.status.textContent = description ? `${description}, ${position}` : position;
    }

    pauseSlideVideo(slide) {
      slide?.querySelectorAll?.('video-player-component').forEach((player) => {
        if (typeof player.pause === 'function') player.pause();
      });
    }

    pauseInactiveVideos(activeSlide) {
      this.querySelectorAll('video-player-component').forEach((player) => {
        if (!activeSlide.contains(player) && typeof player.pause === 'function') player.pause();
      });
    }

    armVideoAutoplay = () => {
      this.videoAutoplayArmed = true;
    };

    hydrateDeferredMedia = () => {
      if (this.deferredMediaHydrated) return;
      this.deferredMediaHydrated = true;
      this.releaseSlideGates();
      for (const template of this.querySelectorAll(
        'template[data-product-media-image-template], template[data-video-poster-template]'
      )) {
        const parent = template.parentElement;
        if (!parent) continue;
        parent.insertBefore(template.content.cloneNode(true), template);
        template.remove();
      }
      loadMediaSwap()
        .then(({ prefetchSwapImages }) => {
          if (this.isConnected) prefetchSwapImages(this.engagedGalleryImages());
        })
        .catch(() => {});
    };

    syncActiveVideo(activeSlide) {
      if (!(activeSlide instanceof HTMLElement)) {
        this.pauseAllVideos();
        return;
      }

      this.pauseInactiveVideos(activeSlide);
      if (
        this.dataset.mediaSwapped === 'true'
        || this.motionPreference?.matches
        || document.hidden
        || !this.videoAutoplayArmed
      ) {
        this.pauseSlideVideo(activeSlide);
        return;
      }

      activeSlide.querySelectorAll('video-player-component').forEach((player) => {
        if (typeof player.playAutoplay === 'function') player.playAutoplay();
      });
    }

    pauseAllVideos() {
      this.querySelectorAll?.('video-player-component').forEach((player) => {
        if (typeof player.pause === 'function') player.pause();
      });
    }

    scrollThumbnails(direction) {
      if (!this.thumbsViewport || !this.activeThumbs?.length) return;

      const firstThumb = this.activeThumbs[0];
      const gap = Number.parseFloat(getComputedStyle(this.thumbsViewport).columnGap) || 0;
      const distance = (firstThumb?.getBoundingClientRect().width || 0) + gap;
      const delta = direction === 'previous' ? -distance : distance;
      this.scrollThumbsTo(this.thumbScrollDestination() + delta, true);
    }

    thumbScrollDestination() {
      if (!this.thumbsViewport) return 0;
      return this.thumbScrollTarget ?? this.thumbsViewport.scrollLeft;
    }

    scrollThumbsTo(left, smooth) {
      if (!this.thumbsViewport) return;

      const maxScroll = Math.max(
        0,
        this.thumbsViewport.scrollWidth - this.thumbsViewport.clientWidth
      );
      const target = Math.min(Math.max(left, 0), maxScroll);
      if (Math.abs(target - this.thumbScrollDestination()) < 1) return;

      this.thumbScrollTarget = target;
      const behavior = smooth && !this.motionPreference?.matches ? 'smooth' : 'auto';
      window.DieuxFrame.write(() => {
        if (!this.isConnected || !this.thumbsViewport) return;
        this.thumbsViewport.scrollTo({ left: target, behavior });
      });
      if (this.thumbScrollSettleTimer) window.clearTimeout(this.thumbScrollSettleTimer);
      this.thumbScrollSettleTimer = window.setTimeout(this.releaseThumbScroll, 700);
    }

    releaseThumbScroll = () => {
      if (this.thumbScrollSettleTimer) window.clearTimeout(this.thumbScrollSettleTimer);
      this.thumbScrollSettleTimer = undefined;
      this.thumbScrollTarget = undefined;
    };

    updateThumbScrollState = () => {
      if (!this.thumbsViewport || !this.thumbsShell) return;

      this.thumbScrollStateKey ??= Symbol('product-media-thumb-scroll-state');
      window.DieuxFrame.measure(this.thumbScrollStateKey, () => {
        if (this.isConnected) this.readThumbScrollState();
      });
    };

    readThumbScrollState() {
      if (!this.thumbsViewport || !this.thumbsShell) return;

      const activeControl = document.activeElement?.closest?.('[data-product-media-thumb-scroll]');
      const tolerance = 1;
      if (
        this.thumbScrollTarget !== undefined
        && Math.abs(this.thumbsViewport.scrollLeft - this.thumbScrollTarget) <= tolerance
      ) {
        this.releaseThumbScroll();
      }
      const canScrollPrevious = this.thumbsViewport.scrollLeft > tolerance;
      const canScrollNext =
        this.thumbsViewport.scrollWidth
          - this.thumbsViewport.clientWidth
          - this.thumbsViewport.scrollLeft
        > tolerance;
      const previousButton = this.querySelector('[data-product-media-thumb-scroll="previous"]');
      const nextButton = this.querySelector('[data-product-media-thumb-scroll="next"]');

      window.DieuxFrame.write(() => {
        if (!this.isConnected || !this.thumbsShell) return;

        this.thumbsShell.dataset.canScrollPrevious = String(canScrollPrevious);
        this.thumbsShell.dataset.canScrollNext = String(canScrollNext);
        if (previousButton instanceof HTMLButtonElement) {
          previousButton.hidden = !canScrollPrevious;
          previousButton.disabled = !canScrollPrevious;
        }
        if (nextButton instanceof HTMLButtonElement) {
          nextButton.hidden = !canScrollNext;
          nextButton.disabled = !canScrollNext;
        }

        if (
          activeControl instanceof HTMLButtonElement
          && (activeControl.hidden || activeControl.disabled)
        ) {
          const fallback = activeControl === previousButton ? nextButton : previousButton;
          if (fallback instanceof HTMLButtonElement && !fallback.hidden && !fallback.disabled) {
            fallback.focus({ preventScroll: true });
          }
        }
      });
    }

    refreshBadgeState = () => {
      const currentVariantId = String(this.dataset.currentVariantId || '');
      const activeWrappers = [];

      this.querySelectorAll('[data-product-media-badges-variant]').forEach((wrapper) => {
        const active = wrapper.dataset.productMediaBadgesVariant === currentVariantId;
        wrapper.hidden = !active;
        if (active) activeWrappers.push(wrapper);
      });

      activeWrappers.forEach((wrapper) => {
        wrapper.querySelectorAll('product-badge-group').forEach((group) => {
          group.refresh?.();
        });
      });
    };

    handleVisibilityChange = () => {
      if (document.hidden) {
        this.pauseAllVideos();
        return;
      }
      this.refreshBadgeState();
      this.syncActiveVideo(this.getActiveSlide());
    };

    handleMotionPreference = () => {
      if (!this.motionPreference.matches) {
        this.syncActiveVideo(this.getActiveSlide());
        return;
      }

      this.pauseAllVideos();
      const pendingSwapState = this.pendingSwapState;
      const hadMediaTransition = this.hasAttribute('data-media-transitioning');
      const instance = this.carouselController?.instance;
      this.cancelTransition();
      if (typeof pendingSwapState === 'boolean') {
        this.setFeaturedMediaSwapped(pendingSwapState, false);
      }
      this.pendingSwapState = undefined;
      this.removeAttribute('data-media-swapping');
      if (hadMediaTransition && instance && !instance.destroyed) {
        this.syncFromSwiper(instance, false);
      }
    };

    handlePhysicalDragStart = () => {
      this.armVideoAutoplay();
      if (
        this.hasAttribute('data-media-swapping')
        || this.hasAttribute('data-media-transitioning')
      ) {
        this.cancelTransition();
      }
    };

    handleStaticLayoutPreference = () => {
      if (!this.staticLayoutPreference.matches) return;

      const alternateOwnedFocus =
        this.alternateButton instanceof HTMLElement
        && this.alternateButton.contains(document.activeElement);
      this.cancelTransition();
      this.resetFeaturedMedia(false);
      if (alternateOwnedFocus) this.viewport?.focus?.({ preventScroll: true });
    };

    handleAlternateBesideLayoutPreference = () => {
      this.releaseSlideGates();
      this.applyVariant(this.dataset.currentVariantId || '', false);
    };

    releaseSlideGates() {
      this.querySelectorAll('[data-product-media-slide] [data-product-media-deferred-source]').forEach(
        (gate) => gate.remove()
      );
    }
  }

  customElements.define('dieux-product-media-gallery', DieuxProductMediaGallery);
})();
